import React, { useEffect, useState } from 'react';
import { products as localProducts } from '../data/products';
import { listProducts } from '../services/products';
import { getCategories, getSettings } from '../services/settings';

const ProductSection = ({ onOrderClick, onAddToCart, onViewProduct }) => {
  const makeSlug = (p) => {
    const name = p?.name_bn || 'product';
    const idFull = (p?.id || '').toString();
    try { return `/product/${encodeURIComponent(name)}-${idFull}`; } catch { return `/product/${name}-${idFull}`; }
  };
  const [items, setItems] = useState(localProducts);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('সব পণ্য');
  const [categories, setCategories] = useState(['সব পণ্য', 'আচার', 'মিষ্টি', 'খিচুড়ি', 'তরকারি']);
  const [settings, setSettings] = useState({ whatsapp_number: '+8801734562313' });

  useEffect(() => {
    (async () => {
      try {
        // Load categories from database
        const dbCategories = await getCategories();
        if (dbCategories && dbCategories.length > 0) {
          setCategories(['সব পণ্য', ...dbCategories]);
        }
        // Load settings for WhatsApp
        const settingsData = await getSettings();
        if (settingsData) {
          setSettings(settingsData);
        }
      } catch (err) {
        console.error('Failed to load categories or settings:', err);
        // Keep default categories
      }
    })();
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('হ্যালো! আমি ঘরোয়া খাবার সম্পর্কে জানতে চাই।');
    const whatsappUrl = `https://wa.me/${settings.whatsapp_number?.replace(/\D/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    (async () => {
      try {
        const rows = await listProducts();
        if (rows && rows.length) {
          // Map backend fields to existing UI shape
          const mapped = rows.map((p) => ({
            id: p.id,
            name_bn: p.name_bn,
            name_en: p.name_en,
            description_bn: p.description_bn,
            price: p.price,
            offer_price: p.offer_price,
            image: p.image_url || '/images/garlic-pickle.jpg',
            category: p.category || 'আচার'
          }));
          setItems(mapped);
        }
      } catch (_err) {
        // fallback to local products already set
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter products based on selected category
  const filteredItems = selectedCategory === 'সব পণ্য' 
    ? (loading ? localProducts : items)
    : (loading ? localProducts : items).filter(product => product.category === selectedCategory);

  // Get product count for each category
  const getCategoryCount = (category) => {
    if (category === 'সব পণ্য') return (loading ? localProducts : items).length;
    return (loading ? localProducts : items).filter(product => product.category === category).length;
  };

  return (
    <section id="products" className="py-8 sm:py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 bengali-text">
            আমাদের পণ্য
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto bengali-text px-2">
            মায়ের হাতে তৈরি সুস্বাদু আচার ও ঘরোয়া খাবার। প্রতিটি পণ্য তৈরি হয় প্রাচীন রেসিপি অনুসারে।
          </p>
        </div>

        {/* Category Filter Buttons - Scrollable on Mobile */}
        <div className="mb-8 sm:mb-12">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
            {categories.map((category) => {
              const count = getCategoryCount(category);
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 bengali-text flex items-center gap-2 whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                      : 'bg-white text-gray-700 hover:bg-orange-50 border-2 border-gray-200'
                  }`}
                >
                  {category}
                  {count > 0 && (
                    <span className={`text-xs px-2 py-0.5 sm:py-1 rounded-full ${
                      selectedCategory === category
                        ? 'bg-white/20 text-white'
                        : 'bg-orange-500 text-white'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Products Grid - Mobile First */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {filteredItems.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-xl text-gray-500 bengali-text">
                এই বিভাগে কোনো পণ্য পাওয়া যায়নি।
              </p>
            </div>
          ) : (
            filteredItems.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2 overflow-hidden border border-gray-100"
            >
              {/* Product Image */}
              <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                <a href={makeSlug(product)} onClick={(e) => { e.preventDefault(); (onViewProduct || onOrderClick)(product); }}>
                  <img
                    src={product.image}
                    alt={product.name_bn}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </a>
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold bengali-text shadow-lg">
                  {product.category}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4 sm:p-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 bengali-text">
                  <a href={makeSlug(product)} onClick={(e) => { e.preventDefault(); (onViewProduct || onOrderClick)(product); }} className="hover:text-orange-600">
                    {product.name_bn}
                  </a>
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 bengali-text line-clamp-2">
                  {product.description_bn}
                </p>
                
                {/* Price and Order Button */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                  <div className="bengali-text">
                    {product.offer_price ? (
                      <>
                        <div className="text-2xl sm:text-3xl font-bold text-orange-600">৳{product.offer_price}</div>
                        <div className="text-base sm:text-lg text-gray-400 line-through">৳{product.price}</div>
                      </>
                    ) : (
                      <div className="text-2xl sm:text-3xl font-bold text-orange-600">৳{product.price}</div>
                    )}
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => onAddToCart && onAddToCart(product)}
                      className="flex-1 sm:flex-none bg-white border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all duration-300 bengali-text shadow-sm"
                    >
                      ঝুড়িতে নিন
                    </button>
                    <button
                      onClick={() => onOrderClick(product)}
                      className="flex-1 sm:flex-none bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 bengali-text shadow-md hover:shadow-lg"
                    >
                      অর্ডার করুন
                    </button>
                  </div>
                </div>
              </div>
            </div>
            ))
          )}
        </div>

        {/* Call to Action - Hidden on mobile (bottom nav handles this) */}
        <div className="text-center mt-12 sm:mt-16 hidden md:block">
          <p className="text-base sm:text-lg text-gray-600 mb-6 bengali-text">
            আরও জানতে চান? আমাদের সাথে যোগাযোগ করুন
          </p>
          <button
            onClick={handleWhatsAppClick}
            className="inline-flex items-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl bengali-text"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99 0-3.951-.488-5.688-1.44l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-2.55-.978-4.95-2.756-6.77-1.778-1.82-4.144-2.82-6.694-2.82-5.448 0-9.886 4.434-9.889 9.885-.001 2.55.978 4.95 2.756 6.77 1.778 1.82 4.144 2.82 6.694 2.82zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
            হোয়াটসঅ্যাপে যোগাযোগ করুন
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
